import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ActorRun {
  id: string;
  status: string;
  startedAt: string;
  finishedAt?: string;
  defaultDatasetId?: string;
  stats?: {
    inputBodyLen?: number;
    outputBodyLen?: number;
  };
  usageTotalUsd?: number;
}

interface ActorRunsTableProps {
  runs: ActorRun[];
  onViewDataset?: (datasetId: string) => void;
  selectedRunId?: string | null;
  onRunClick?: (runId: string) => void;
}

export function ActorRunsTable({ runs, onViewDataset, selectedRunId, onRunClick }: ActorRunsTableProps) {
  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "succeeded":
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case "failed":
      case "aborted":
        return <XCircle className="w-4 h-4 text-red-600" />;
      case "running":
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    let variant: "default" | "secondary" | "destructive" | "outline" = "default";
    let className = "";

    switch (statusLower) {
      case "succeeded":
        className = "bg-green-500/10 text-green-700 border-green-500/20";
        break;
      case "failed":
        className = "bg-red-500/10 text-red-700 border-red-500/20";
        break;
      case "running":
        className = "bg-blue-500/10 text-blue-700 border-blue-500/20";
        break;
      case "aborted":
        className = "bg-orange-500/10 text-orange-700 border-orange-500/20";
        break;
      default:
        className = "bg-gray-500/10 text-gray-700 border-gray-500/20";
    }

    return (
      <Badge variant="outline" className={cn("text-xs font-medium", className)}>
        {status}
      </Badge>
    );
  };

  const formatDuration = (startedAt: string, finishedAt?: string) => {
    const start = new Date(startedAt);
    const end = finishedAt ? new Date(finishedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (runs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-sm">No runs found for this actor</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-xs uppercase text-muted-foreground font-medium">
          <tr>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Run ID</th>
            <th className="px-4 py-3 text-left">Started</th>
            <th className="px-4 py-3 text-left">Duration</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-card">
          {runs.map((run) => {
            const isSelected = selectedRunId === run.id;
            return (
              <tr
                key={run.id}
                className={cn(
                  "hover:bg-secondary/20 cursor-pointer transition-colors",
                  isSelected && "bg-primary/10 border-l-2 border-l-primary"
                )}
                onClick={() => onRunClick?.(run.id)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(run.status)}
                    {getStatusBadge(run.status)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <code className="text-xs font-mono text-muted-foreground">{run.id.slice(0, 12)}...</code>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{formatDate(run.startedAt)}</td>
                <td className="px-4 py-3 text-muted-foreground">{formatDuration(run.startedAt, run.finishedAt)}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  {onViewDataset ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => onViewDataset(run.id)}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      View Dataset
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">Click to view</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
